import React, { useState } from 'react';
import { Copy, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const UpiPayment = ({ upiId, qrCodeBase64, amountInr, courseName }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(upiId);
    setCopied(true);
    toast.success('UPI ID copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="upi-payment-component">
      <div className="bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-200 rounded-3xl p-8 shadow-2xl">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-gray-900 mb-2">Pay with UPI</h3>
          <p className="text-gray-600">
            Scan QR code or copy UPI ID to pay in Indian Rupees
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 mb-6 shadow-lg">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              {qrCodeBase64 ? (
                <div className="relative">
                  <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-green-400 via-emerald-400 to-green-400 rounded-2xl blur-xl opacity-30" />
                  <img
                    src={qrCodeBase64}
                    alt="UPI QR Code"
                    className="relative w-64 h-64 rounded-2xl border-4 border-white shadow-xl"
                    data-testid="upi-qr-code-image"
                  />
                </div>
              ) : (
                <div className="w-64 h-64 bg-gray-100 rounded-2xl flex items-center justify-center border-4 border-gray-200">
                  <p className="text-gray-400 text-center px-4">UPI QR Code not configured</p>
                </div>
              )}
            </div>
          </div>

          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-xl font-bold text-gray-500">INR</span>
              <span className="text-5xl font-bold text-gray-900">{amountInr.toLocaleString('en-IN')}</span>
            </div>
            <p className="text-sm text-gray-500">Total Amount</p>
          </div>

          {upiId && (
            <div className="border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">UPI ID:</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={upiId}
                  readOnly
                  className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm"
                  data-testid="upi-id-display"
                />
                <button
                  onClick={copyToClipboard}
                  className="p-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                  data-testid="copy-upi-btn"
                >
                  {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-900 mb-1">Payment Instructions</h4>
              <ol className="text-sm text-amber-800 space-y-1 list-decimal list-inside">
                <li>Open any UPI app (Google Pay, PhonePe, Paytm, etc.)</li>
                <li>Scan the QR code or enter the UPI ID</li>
                <li>Send exactly INR {amountInr.toLocaleString('en-IN')}</li>
                <li>Keep your transaction/UTR number for verification</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>Payment verification may take a few minutes</p>
          <p>You'll receive an email once your payment is confirmed</p>
        </div>
      </div>
    </div>
  );
};

export default UpiPayment;
